const VALUELESS_OPERATORS = ['isNull', 'notNull', 'isTrue', 'isFalse', 'isFixed']
const KNOWN_OPERATORS = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'contains', 'like', 'startsWith', 'endsWith', 'in', 'notIn', 'isNull', 'notNull', 'isTrue', 'isFalse', 'isFixed']

class CriteriaConfigError extends Error {
  constructor (message) {
    super(message)
    this.name = 'CriteriaConfigError'
  }
}

function requiredKey (value, label) {
  const normalized = typeof value === 'string' ? value.trim() : ''
  const unsafe = normalized.split('.').some(part => !part || ['__proto__', 'prototype', 'constructor'].indexOf(part) !== -1)
  if (!normalized || normalized === 'undefined' || unsafe) {
    throw new CriteriaConfigError(`Clave Criteria inválida para ${label}`)
  }
  return normalized
}

function fieldIdentity (field) {
  return requiredKey(field.id || field.key, field.label || 'filtro')
}

function validateFieldIdentities (fields) {
  const identities = new Set()
  ;(fields || []).forEach(field => {
    const identity = fieldIdentity(field)
    if (identities.has(identity)) throw new CriteriaConfigError(`Identidad Criteria duplicada: ${identity}`)
    identities.add(identity)
  })
}

function prefixKey (field, key) {
  const normalizedKey = requiredKey(key, field.label || fieldIdentity(field))
  if (field.prefix === undefined || field.prefix === null || field.prefix === '') return normalizedKey
  const prefix = requiredKey(field.prefix, `${field.label || fieldIdentity(field)}.prefix`)
  return normalizedKey === prefix || normalizedKey.indexOf(`${prefix}.`) === 0
    ? normalizedKey
    : `${prefix}.${normalizedKey}`
}

function monthRange (value) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(value))
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  if (year < 1 || month < 1 || month > 12) return null
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return {
    from: `${match[1]}-${match[2]}-01`,
    to: `${match[1]}-${match[2]}-${String(days[month - 1]).padStart(2, '0')}`
  }
}

function queryKey (field, operator, explicitKey, forceOperator, applyOperatorToExplicit) {
  const hasExplicit = explicitKey !== undefined && explicitKey !== null && explicitKey !== ''
  const base = hasExplicit
    ? explicitKey
    : typeof field.query === 'string'
      ? field.query
      : field.keyTarget || field.key || field.id
  const query = prefixKey(field, base)
  if (!operator || (hasExplicit && !forceOperator && !applyOperatorToExplicit)) return query
  const parts = query.split('.')
  const hasOperator = KNOWN_OPERATORS.indexOf(parts[parts.length - 1]) !== -1
  if (hasOperator && !forceOperator && field.operator === undefined) return query
  if (hasOperator) parts.pop()
  return `${parts.join('.')}.${operator}`
}

function selectedValue (value, field) {
  if (!field.fieldKey) return value
  const extract = item => {
    if (!item || typeof item !== 'object') return item
    if (!Object.prototype.hasOwnProperty.call(item, field.fieldKey) || item[field.fieldKey] === undefined) {
      throw new CriteriaConfigError(`fieldKey ${field.fieldKey} ausente para ${field.label || fieldIdentity(field)}`)
    }
    return item[field.fieldKey]
  }
  return Array.isArray(value) ? value.map(extract) : extract(value)
}

function hasValue (value) {
  return value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)
}

function completePair (field, firstName, secondName, queryFirst, querySecond, type) {
  const first = field[firstName]
  const second = field[secondName]
  const hasFirst = first !== undefined && first !== null && first !== ''
  const hasSecond = second !== undefined && second !== null && second !== ''
  if (hasFirst !== hasSecond) throw new CriteriaConfigError(`${type} requiere ${firstName} y ${secondName}`)
  if (hasFirst) return [first, second]
  const query = typeof field.query === 'object' && field.query ? field.query : {}
  const fromQuery = query[queryFirst]
  const toQuery = query[querySecond]
  const hasQueryFirst = fromQuery !== undefined && fromQuery !== null && fromQuery !== ''
  const hasQuerySecond = toQuery !== undefined && toQuery !== null && toQuery !== ''
  if (hasQueryFirst !== hasQuerySecond) throw new CriteriaConfigError(`${type} requiere query.${queryFirst} y query.${querySecond}`)
  return hasQueryFirst ? [fromQuery, toQuery] : null
}

function buildCriteria (config, values) {
  const fields = config.fields || []
  validateFieldIdentities(fields)
  const criteria = Object.create(null)
  ;fields.forEach(field => {
    const localKey = fieldIdentity(field)
    const value = values[localKey]
    const operator = field.operator || 'eq'

    if (field.type === 'month') {
      const keys = completePair(field, 'keyDesde', 'keyHasta', 'from', 'to', 'month')
      if (!keys) throw new CriteriaConfigError('month requiere dos claves de destino')
      const range = monthRange(value)
      if (!range) return
      criteria[prefixKey(field, keys[0])] = range.from
      criteria[prefixKey(field, keys[1])] = range.to
      return
    }

    if (field.type === 'date_range') {
      const keys = completePair(field, 'keyDesde', 'keyHasta', 'from', 'to', 'date_range')
      const from = Array.isArray(value) ? value[0] : value && (value.from || value.desde)
      const to = Array.isArray(value) ? value[1] : value && (value.to || value.hasta)
      if (!hasValue(from) || !hasValue(to)) return
      criteria[queryKey(field, 'gte', keys && keys[0], true)] = from
      criteria[queryKey(field, 'lte', keys && keys[1], true)] = to
      return
    }

    if (field.type === 'select_input') {
      const keys = completePair(field, 'keySelect', 'keyInput', 'select', 'input', 'select_input')
      if (!keys) throw new CriteriaConfigError('select_input requiere dos claves de destino')
      const select = value && typeof value === 'object' ? value.select : undefined
      const input = value && typeof value === 'object' ? value.input : undefined
      if (hasValue(select)) criteria[queryKey(field, operator, keys[0], false, true)] = selectedValue(select, field)
      if (hasValue(input)) criteria[queryKey(field, operator, keys[1], false, true)] = input
      return
    }

    const key = queryKey(field, operator)
    if (!hasValue(value) && VALUELESS_OPERATORS.indexOf(operator) === -1) return
    criteria[key] = VALUELESS_OPERATORS.indexOf(operator) === -1 ? selectedValue(value, field) : ''
  })
  return criteria
}

function serializeCriteria (criteria) {
  const params = new URLSearchParams()
  Object.keys(criteria).forEach(key => {
    const normalizedKey = requiredKey(key, 'serialización')
    const value = criteria[key]
    if (value === undefined) throw new CriteriaConfigError(`Valor Criteria indefinido para ${normalizedKey}`)
    const serialized = Array.isArray(value) ? value.join('|') : value
    params.append(normalizedKey, serialized === null ? '' : String(serialized))
  })
  return params.toString()
}

function initialValue (field) {
  if (field.defaultValue !== undefined) return field.defaultValue
  if (field.type === 'select' && field.multiple) return []
  if (field.type === 'date_range') return []
  if (field.type === 'select_input') return { select: '', input: '' }
  if (field.type === 'switch') return false
  return ''
}

function initialCriteriaValues (config) {
  const fields = config.fields || []
  validateFieldIdentities(fields)
  return fields.reduce((values, field) => {
    values[fieldIdentity(field)] = initialValue(field)
    return values
  }, {})
}

export { CriteriaConfigError, VALUELESS_OPERATORS, buildCriteria, fieldIdentity, hasValue, initialCriteriaValues, monthRange, prefixKey, queryKey, serializeCriteria, validateFieldIdentities }
